import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Loader2, Wand2, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { newKey, recordsError } from "../lib/records";
import { ClassificationField, PartySearch, ReadersPicker, usePeople } from "../components/records/RecordFields";
import { TemplatePreviewFrame } from "../components/records/TemplateBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const NONE = "__none__";

function Row({ id, label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

// « Générer un document » (Ordre 18-4): a published template + a client pharmacy (+ its subscription) → a preview of the real page →
// the PDF, registered as the first version of a NEW draft document. Nothing is created before « Générer le PDF ».
export default function RecordGenerate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const people = usePeople();
  const [templates, setTemplates] = useState(null);
  const [templateId, setTemplateId] = useState(params.get("modele") || "");
  const [pharmacy, setPharmacy] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [title, setTitle] = useState("");
  const [classification, setClassification] = useState("internal");
  const [readers, setReaders] = useState([]);
  const [responsible, setResponsible] = useState(user?.id || "");
  const [representatives, setRepresentatives] = useState({ items: [], company_setting: null });
  const [representativeId, setRepresentativeId] = useState("");
  const [confirmRepresentative, setConfirmRepresentative] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const key = useRef(newKey());
  const seq = useRef(0);
  const seesRevenue = !!user?.features?.billing && hasPermission(user, "billing:view");

  useEffect(() => {
    api.get("/records/templates").then(({ data }) => setTemplates(data.items.filter((t) => t.active && t.published_number))).catch(() => setTemplates([]));
  }, []);
  // Verified mandates in force the person may read (often none: then the company setting is the only source of the legal representative).
  useEffect(() => {
    api.get("/records/legal-representatives").then(({ data }) => setRepresentatives(data)).catch(() => setRepresentatives({ items: [], company_setting: null }));
  }, []);
  useEffect(() => {
    setSubscriptionId("");
    setSubscriptions([]);
    if (!pharmacy || !seesRevenue) return;
    api.get("/billing/subscriptions", { params: { prospect_id: pharmacy.ref, limit: 50 } })
      .then(({ data }) => setSubscriptions(data.items.filter((s) => s.status !== "cancelled"))).catch(() => setSubscriptions([]));
  }, [pharmacy, seesRevenue]);

  // Anything changed: a new key (a retry of the SAME choice reuses it, so a double click never makes two documents) and a new preview.
  const choice = useMemo(() => ({ template_id: templateId, prospect_id: pharmacy?.ref || null, subscription_id: subscriptionId || null,
    legal_representative_mandate_id: representativeId || null }), [templateId, pharmacy, subscriptionId, representativeId]);
  useEffect(() => setConfirmRepresentative(false), [representativeId]);
  useEffect(() => {
    key.current = newKey();
    if (!choice.template_id) { setPreview(null); return undefined; }
    const mine = ++seq.current;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(`/records/templates/${choice.template_id}/preview`, { prospect_id: choice.prospect_id,
          subscription_id: choice.subscription_id, legal_representative_mandate_id: choice.legal_representative_mandate_id, paged: true });
        if (mine === seq.current) { setPreview(data); setPreviewError(""); }
      } catch (e) {
        if (mine === seq.current) { setPreview(null); setPreviewError(recordsError(e, "Aperçu indisponible.")); }
      } finally {
        if (mine === seq.current) setPreviewLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [choice]);

  const template = (templates || []).find((t) => t.id === templateId);
  const responsibleOptions = people.filter((p) => classification !== "confidential" || p.confidential);
  const missing = preview?.missing || [];
  const companyMissing = missing.some((m) => m.group === "Société");
  const chosenRepresentative = representatives.items.find((r) => r.mandate_id === representativeId);
  const ready = template && preview && missing.length === 0 && !previewLoading && (classification !== "restricted" || readers.length > 0) && (!representativeId || confirmRepresentative);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/records/documents/generate", { ...choice, confirm_legal_representative: !!representativeId && confirmRepresentative, title: title.trim(),
        classification, reader_ids: readers, responsible_user_id: responsible || null }, { headers: { "Idempotency-Key": key.current } });
      toast.success(`Document ${data.number} créé en brouillon avec son PDF.`);
      navigate(`/documents/${data.id}`);
    } catch (e) {
      setError(recordsError(e, "Génération impossible. Réessayez."));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="generate-page">
      <Link to="/documents/modeles" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ChevronLeft size={14} />Modèles de documents</Link>
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Générer un document</h1>
        <p className="text-sm text-slate-500">Le PDF devient la première version d'un nouveau document en brouillon, qui suivra le circuit habituel (revue, approbation, signature).</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] lg:items-start">
        <Card className="p-4 sm:p-5 space-y-4">
          <Row id="generate-template" label="Modèle">
            <Select value={templateId || NONE} onValueChange={(v) => setTemplateId(v === NONE ? "" : v)}>
              <SelectTrigger id="generate-template" className="h-10" data-testid="generate-template"><SelectValue placeholder="Choisir un modèle…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Choisir un modèle…</SelectItem>
                {(templates || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} (version {t.published_number})</SelectItem>)}
              </SelectContent>
            </Select>
            {templates && templates.length === 0 && <p className="text-xs text-slate-500">Aucun modèle publié pour le moment.</p>}
          </Row>
          <Row id="generate-pharmacy" label="Pharmacie cliente">
            {pharmacy ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="generate-pharmacy">
                <span className="font-medium text-slate-900">{pharmacy.name}</span>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Changer de pharmacie" onClick={() => setPharmacy(null)}><X size={14} /></Button>
              </div>
            ) : <PartySearch kind="customer" onPick={setPharmacy} />}
          </Row>
          {pharmacy && seesRevenue && (
            <Row id="generate-subscription" label="Abonnement" hint="Facultatif : remplit l'offre, le prix, la périodicité et la date de début.">
              <Select value={subscriptionId || NONE} onValueChange={(v) => setSubscriptionId(v === NONE ? "" : v)}>
                <SelectTrigger id="generate-subscription" className="h-10" data-testid="generate-subscription"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Aucun abonnement</SelectItem>
                  {subscriptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.number} · {s.offer?.offer_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Row>
          )}
          <Row id="generate-title" label="Titre du document" hint={template ? `Par défaut : « ${template.name}${pharmacy ? ` — ${pharmacy.name}` : ""} ».` : null}>
            <Input id="generate-title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} className="h-10" data-testid="generate-title" />
          </Row>
          <div className="space-y-1.5">
            <Label>Confidentialité</Label>
            <ClassificationField value={classification} onChange={setClassification} canConfidential={hasPermission(user, "records:view_confidential")} />
          </div>
          {classification === "restricted" && <ReadersPicker people={people} value={readers} onChange={setReaders} classification={classification} />}
          <Row id="generate-responsible" label="Responsable">
            <Select value={responsible} onValueChange={setResponsible}>
              <SelectTrigger id="generate-responsible" className="h-10"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{responsibleOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
          {representatives.items.length > 0 && (
            <div className="space-y-2" data-testid="generate-representative">
              <Row id="generate-representative" label="Représentant légal" hint="Facultatif. Choisi parmi les mandats en cours et vérifiés ; il ne change rien aux réglages de la société.">
                <Select value={representativeId || NONE} onValueChange={(v) => setRepresentativeId(v === NONE ? "" : v)}>
                  <SelectTrigger id="generate-representative" className="h-10" data-testid="generate-representative-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{representatives.company_setting ? `Réglage de la société : ${representatives.company_setting}` : "Réglage de la société (non renseigné)"}</SelectItem>
                    {representatives.items.map((r) => <SelectItem key={r.mandate_id} value={r.mandate_id}>{r.text}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Row>
              {chosenRepresentative && (
                <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="generate-representative-compare">
                  <p>Ce document nommera <span className="font-medium">{chosenRepresentative.text}</span>.</p>
                  <p className="text-xs text-slate-600">Réglage de la société, inchangé : {representatives.company_setting || "non renseigné"}.</p>
                  <label className="flex min-h-11 items-start gap-2 text-sm text-slate-800">
                    <input type="checkbox" className="mt-1 h-4 w-4" checked={confirmRepresentative} onChange={(e) => setConfirmRepresentative(e.target.checked)} data-testid="generate-representative-confirm" />
                    Je confirme ce représentant légal pour ce document seulement.
                  </label>
                </div>
              )}
            </div>
          )}
          {companyMissing && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="generate-company-missing">
              L'identité de la société n'est pas renseignée.{" "}
              {hasPermission(user, "settings:manage") ? <Link to="/settings/records" className="font-medium underline">Paramètres › Documents</Link> : "Demandez à un administrateur de la saisir dans Paramètres › Documents."}
            </p>
          )}
          {error && <p className="text-sm text-red-600" role="alert" data-testid="generate-error">{error}</p>}
          <Button className="w-full" onClick={submit} disabled={!ready || busy} data-testid="generate-submit">
            {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Wand2 size={14} className="mr-1.5" />}Générer le PDF
          </Button>
          {template && !ready && !busy && missing.length > 0 && (
            <p className="text-xs text-slate-500">Toutes les valeurs surlignées dans l'aperçu doivent être connues avant de générer.</p>
          )}
        </Card>
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-900">Aperçu</h2>
          {templateId ? <TemplatePreviewFrame preview={preview} loading={previewLoading} error={previewError} />
            : <Card className="p-8 text-center text-sm text-slate-500">Choisissez un modèle pour voir le document.</Card>}
        </div>
      </div>
    </div>
  );
}
